import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { z } from 'zod'
import { Megaphone } from 'lucide-react'
import { Callout } from '@/components/ui/callout'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useAdsSetup } from '@/hooks/useAds'
import { AdAccountTable } from '@/components/ads/AdAccountTable'
import { AdConnectionList } from '@/components/ads/AdConnectionList'
import { AdCampaignTable } from '@/components/ads/AdCampaignTable'
import { CompanyAdsTable } from '@/components/ads/CompanyAdsTable'
import { ConnectGoogleAdsButton, GoogleSignInNote } from '@/components/ads/ConnectGoogleAds'
import { adSignInDoneDtoSchema, type AdSignInDoneDto, type AdsSetupDto } from '@lib/dto/ads.dto'

/** What the return page hands over after a sign-in, carried in the router's location state. */
const arrivalSchema = z.object({ signedIn: adSignInDoneDtoSchema })

/** The one-line report of the sign-in the person just finished. */
function SignedInNotice({ done, setup }: { done: AdSignInDoneDto; setup: AdsSetupDto }) {
  const connection = setup.connections.find((c) => c.id === done.connectionId)
  const who = connection?.email ?? 'Google'
  if (done.accountsPending) {
    return (
      <Callout tone="attention" title={`Connected as ${who}`}>
        Its Google Ads accounts couldn’t be listed just now. They’ll appear here by tomorrow, without signing in again.
      </Callout>
    )
  }
  if (connection !== undefined && connection.accountCount === 0) {
    return (
      <Callout tone="attention" title={`Connected as ${who}`}>
        This Google login doesn’t reach any Google Ads accounts. Sign in with one that has your manager account, or the
        venue’s own account.
      </Callout>
    )
  }
  const found = done.newAccountIds.length
  let detail: string
  if (found === 0) detail = 'No new accounts: everything it reaches was already here.'
  else detail = found === 1 ? 'It found 1 new account, marked below.' : `It found ${String(found)} new accounts, marked below.`
  return (
    <Callout tone="success" title={done.renewed ? `Reconnected as ${who}` : `Connected as ${who}`}>
      {detail}
    </Callout>
  )
}

/**
 * The company's Ads page (docs/ad-analytics.spec.md): connecting Google Ads,
 * and every account the connections reach. Organization admins only, and only
 * for a company with the Ads feature.
 */
export function AdsPage() {
  const location = useLocation()
  const organizationQuery = useOrganization()
  const hasAds = organizationQuery.data?.features.includes('ads') ?? false
  const setupQuery = useAdsSetup(hasAds)
  const arrival = useMemo(() => {
    const parsed = arrivalSchema.safeParse(location.state)
    return parsed.success ? parsed.data.signedIn : null
  }, [location.state])
  const newAccountIds = useMemo(() => new Set(arrival?.newAccountIds ?? []), [arrival])

  if (organizationQuery.isLoading || (hasAds && setupQuery.isPending)) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }

  if (!hasAds) {
    return (
      <PageContainer width="wide">
        <EmptyState icon={Megaphone} title="Ads aren’t turned on" description="Ask CSE to turn them on for your company." />
      </PageContainer>
    )
  }

  const header = <PageHeader title="Ads" meta={organizationQuery.data?.name} />

  if (setupQuery.isError) {
    return (
      <PageContainer width="wide" className="space-y-6">
        {header}
        <ErrorState message={requestErrorMessage(setupQuery.error)} onRetry={() => void setupQuery.refetch()} />
      </PageContainer>
    )
  }
  const setup = setupQuery.data
  if (setup === undefined) return null

  const hasConnections = setup.connections.length > 0
  const inUse = setup.accounts.some((a) => a.use === 'in_use' || a.use === 'stopped')

  return (
    <PageContainer width="wide" className="space-y-8">
      {header}

      {arrival !== null ? <SignedInNotice done={arrival} setup={setup} /> : null}

      {setup.googleAvailable ? null : (
        <Callout tone="attention" title="Google Ads can’t be connected yet">
          CSE is still setting up Google Ads for FLOH Venues. Your numbers will appear here once it is.
        </Callout>
      )}

      {inUse ? (
        <section className="space-y-4">
          <SectionHeader title="Numbers" description="Every venue’s Google Ads, in two-week periods, each compared with the one before." />
          <CompanyAdsTable canRefresh={setup.googleAvailable} />
        </section>
      ) : null}

      <section className="space-y-4">
        <SectionHeader title="Connections" description="Each is one Google sign-in, belonging to the company." />
        {hasConnections ? (
          <AdConnectionList
            connections={setup.connections}
            action={setup.googleAvailable ? <ConnectGoogleAdsButton quiet /> : null}
          />
        ) : null}
        {!hasConnections && setup.googleAvailable ? <ConnectGoogleAdsButton /> : null}
        {!hasConnections && !setup.googleAvailable ? <p className="m-0 text-sm text-ink-muted">No connections yet.</p> : null}
        {setup.googleAvailable ? <GoogleSignInNote /> : null}
      </section>

      <section className="space-y-4">
        <SectionHeader title="Accounts" description="Every Google Ads account your connections reach. Each is counted once, however many connections reach it." />
        <AdAccountTable
          accounts={setup.accounts}
          failingConnectionIds={new Set(setup.connections.filter((c) => c.failingSince !== null).map((c) => c.id))}
          venues={setup.venues}
          newAccountIds={newAccountIds}
          suggestedVenueId={arrival?.venueId ?? null}
        />
      </section>

      {inUse ? (
        <section className="space-y-4">
          <SectionHeader
            title="Campaigns"
            description="Each counts where its account does, unless it is given a place of its own."
          />
          <AdCampaignTable campaigns={setup.campaigns} accounts={setup.accounts} venues={setup.venues} />
        </section>
      ) : null}
    </PageContainer>
  )
}
